import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'core/store';
import {
  IonButton,
  IonContent,
  IonFab,
  IonFabButton,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonModal,
  IonPage,
  IonSpinner,
  IonToolbar
} from '@ionic/react';
import HKSelect from 'components/hk-select/HKSelect';
import {
  userFields,
  ModalMode,
  InputField
} from 'features/shared/account/Account';
import { updateUser } from 'core/services/UserActions';
import { Controller, useForm } from 'react-hook-form';
import {
  EMAIL_REGEX,
  HKPlatform,
  PHONE_REGEX,
  STATE_LIST
} from 'core/constants';
import EditIcon from 'assets/icons/edit.svg';
import { updateHome } from 'core/services/HomeActions';
import { Home } from 'core/services/HomeSlice';
import {
  formatMobilePhoneNumber,
  gaBtnClickEvent,
  gaModalView
} from 'core/util';
import Close from 'assets/icons/close.svg';
import './EditAccount.scss';
import {
  hideDiscardModal,
  hideUserEdit,
  showDiscardModal
} from 'features/shared/account/AccountSlice';
import DiscardChanges from 'features/shared/account/DiscardChanges';

export interface AccountForm {
  [index: string]: string;
}

const EditAccount: React.FC = () => {
  const { loading } = useSelector((state: RootState) => state.loading);
  const { modal_mode, discardModalVisible } = useSelector(
    (state: RootState) => state.account
  );
  const { name, email, mobile_phone, isDigital } = useSelector(
    (state: RootState) => state.user
  );
  const homes = useSelector((state: RootState) => state.home.homes);
  const { platformType, isDesktopWidth } = useSelector(
    (state: RootState) => state.platform
  );
  const [currentHome, setCurrentHome] = useState<Home | null>();
  const [fields, setFields] = useState<InputField[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

  const dispatch = useDispatch();

  const onSubmit = (values: any) => {
    if (modal_mode === ModalMode.User) {
      gaBtnClickEvent('update_edit_account_user');
      dispatch(updateUser(values));
    } else {
      gaBtnClickEvent('update_edit_account_home');
      if (!!currentHome) {
        dispatch(updateHome(values, currentHome.id));
      }
    }
    dispatch(hideUserEdit());
  };

  const cancelEdit = () => {
    gaBtnClickEvent('cancel_edit_account');
    if (hasUnsavedChanges) {
      dispatch(showDiscardModal());
    } else {
      dispatch(hideUserEdit());
      dispatch(hideDiscardModal());
      setHasUnsavedChanges(false);
    }
  };

  const formRule = (id: string) => {
    if (id === 'email') {
      return {
        required: true,
        pattern: {
          value: EMAIL_REGEX,
          message: 'Invalid email'
        }
      };
    }
    if (id === 'mobile_phone') {
      return {
        required: false,
        pattern: {
          value: PHONE_REGEX,
          message: 'Invalid phone number'
        }
      };
    }
    return { required: true };
  };

  const { control, handleSubmit, errors, formState, reset } =
    useForm<AccountForm>({
      defaultValues: {
        name: '',
        email: '',
        mobile_phone: '',
        address1: '',
        city: '',
        state: '',
        postal_code: ''
      },
      mode: 'onChange'
    });

  useEffect(() => {
    if (!!homes) {
      setCurrentHome(homes[0]);
    }
  }, [homes]);

  useEffect(() => {
    if (Object.keys(formState.dirtyFields).length > 0) {
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [formState]);

  useEffect(() => {
    if (!!currentHome) {
      reset({
        name,
        email,
        mobile_phone: formatMobilePhoneNumber(mobile_phone)!,
        address1: currentHome.address1,
        city: currentHome.city,
        state: currentHome.state,
        postal_code: currentHome.postal_code
      });
    }
  }, [currentHome]);

  useEffect(() => {
    setFields(userFields);
    if (modal_mode !== ModalMode.Hidden) {
      gaModalView(isDigital, '/account/edit/contact');
    }
  }, [modal_mode]);

  // component Unmount (cleanup)
  useEffect(() => {
    return () => {
      dispatch(hideUserEdit());
    };
  }, []);

  function DesktopEditAccountView() {
    return (
      <>
        <IonContent className="ion-padding">
          <form onSubmit={handleSubmit(onSubmit)}>
            <IonList className="hk-desktop-account-edit ion-margin">
              <IonListHeader className="ion-no-padding ion-margin-vertical">
                <IonLabel
                  color="dark"
                  className="hk-desktop-account-edit-title"
                >
                  <IonIcon color="dark" icon={EditIcon} />
                  {modal_mode === ModalMode.User && 'Edit contact info'}
                </IonLabel>
              </IonListHeader>
              <div className="hk-desktop-account-edit-form">
                {fields.map((field, i) => {
                  const { id, label } = field;
                  return (
                    <Controller
                      key={`controller-${i}`}
                      control={control}
                      render={({ onChange, onBlur, value }) => (
                        <IonItem
                          lines="none"
                          className="hk-desktop-account-edit-form-item ion-no-padding"
                        >
                          <IonLabel position="stacked" color="medium">
                            {label}
                          </IonLabel>

                          {id !== 'state' &&
                            id !== 'mobile_phone' &&
                            id !== 'email' && (
                              <IonInput
                                className="hk-desktop-account-edit-field ion-margin-bottom"
                                onIonChange={(value) => {
                                  onChange(value);
                                }}
                                value={value}
                                color={errors[field.id] ? 'danger' : 'dark'}
                                placeholder={label}
                                type="text"
                              ></IonInput>
                            )}
                          {id === 'email' && (
                            <IonInput
                              className="hk-desktop-account-edit-field ion-margin-bottom"
                              onIonChange={(value) => {
                                onChange(value);
                              }}
                              value={value}
                              color={errors[field.id] ? 'danger' : 'dark'}
                              placeholder={label}
                              type="text"
                            ></IonInput>
                          )}
                          {id === 'mobile_phone' && (
                            <IonInput
                              className="hk-desktop-digital-new-contact-form-item-field ion-margin-bottom"
                              onIonChange={(e) => {
                                onChange(
                                  formatMobilePhoneNumber(e.detail.value!)
                                );
                              }}
                              value={formatMobilePhoneNumber(value)}
                              color={errors[field.id] ? 'danger' : 'dark'}
                              type="tel"
                              inputMode="tel"
                              pattern="tel"
                            ></IonInput>
                          )}
                          {id === 'state' && (
                            <HKSelect
                              className={`hk-state-select${
                                errors[field.id] ? ' hk-select-error' : ''
                              }`}
                              placeholder="Select state"
                              value={value}
                              options={STATE_LIST.map((state) => ({
                                value: state.abbreviation,
                                label: state.name
                              }))}
                              onChange={(stateValue) => onChange(stateValue)}
                            />
                          )}
                        </IonItem>
                      )}
                      name={id}
                      rules={formRule(id)}
                    />
                  );
                })}
              </div>
            </IonList>
          </form>
          <IonFab vertical="top" horizontal="end">
            <IonFabButton
              color="background"
              className={`hk-fab-button ${
                platformType === HKPlatform.DESKTOP && isDesktopWidth
                  ? ''
                  : 'hk-fab-button-margin-top'
              }`}
              onClick={cancelEdit}
            >
              <IonIcon color="medium" icon={Close} />
            </IonFabButton>
          </IonFab>
        </IonContent>
        <div className="hk-desktop-account-edit-toolbar">
          <IonButton
            expand="block"
            color="white"
            strong={true}
            disabled={loading}
            onClick={cancelEdit}
          >
            Cancel
          </IonButton>
          <IonButton
            expand="block"
            color="primary"
            type="submit"
            strong={true}
            disabled={!formState.isValid || loading}
            onClick={handleSubmit(onSubmit)}
          >
            {loading ? <IonSpinner name="dots" /> : 'Update'}
          </IonButton>
        </div>
      </>
    );
  }

  function MobileEditAccountView() {
    return (
      <>
        <IonContent fullscreen className="ion-padding">
          <form onSubmit={handleSubmit(onSubmit)}>
            <IonList className="hk-account-edit ion-margin">
              <IonListHeader className="ion-no-padding ion-text-center ion-margin-vertical">
                <IonLabel color="dark" className="hk-account-edit-title">
                  <IonIcon color="dark" icon={EditIcon} />
                  {modal_mode === ModalMode.User && 'Edit contact info'}
                </IonLabel>
              </IonListHeader>
              {fields.map((field, i) => {
                const { id, label } = field;
                return (
                  <Controller
                    key={`controller-${i}`}
                    control={control}
                    render={({ onChange, onBlur, value }) => (
                      <IonItem lines="none" className="ion-no-padding">
                        <IonLabel position="stacked" color="medium">
                          {label}
                        </IonLabel>
                        {id !== 'state' &&
                          id !== 'mobile_phone' &&
                          id !== 'email' && (
                            <IonInput
                              className="hk-account-edit-field ion-margin-bottom"
                              onIonChange={(value) => {
                                onChange(value);
                              }}
                              value={value}
                              color={errors[field.id] ? 'danger' : 'dark'}
                              placeholder={label}
                              type="text"
                            ></IonInput>
                          )}
                        {id === 'email' && (
                          <IonInput
                            className="hk-account-edit-field ion-margin-bottom"
                            onIonChange={(value) => {
                              onChange(value);
                            }}
                            value={value}
                            color={errors[field.id] ? 'danger' : 'dark'}
                            placeholder={label}
                            type="text"
                          ></IonInput>
                        )}
                        {id === 'mobile_phone' && (
                          <IonInput
                            className="hk-digital-new-contact-form-item-field ion-margin-bottom"
                            onIonChange={(e) => {
                              onChange(
                                formatMobilePhoneNumber(e.detail.value!)
                              );
                            }}
                            value={formatMobilePhoneNumber(value)}
                            color={errors[field.id] ? 'danger' : 'dark'}
                            type="tel"
                            inputMode="tel"
                            pattern="tel"
                          ></IonInput>
                        )}
                        {id === 'state' && (
                          <HKSelect
                            className={`hk-state-select${
                              errors[field.id] ? ' hk-select-error' : ''
                            }`}
                            placeholder="Select state"
                            value={value}
                            options={STATE_LIST.map((state) => ({
                              value: state.abbreviation,
                              label: state.name
                            }))}
                            onChange={(stateValue) => onChange(stateValue)}
                          />
                        )}
                      </IonItem>
                    )}
                    name={id}
                    rules={formRule(id)}
                  />
                );
              })}
            </IonList>
          </form>
        </IonContent>
        <IonToolbar className="hk-mobile-account-edit-toolbar ion-padding">
          <IonButton
            expand="block"
            color="white"
            strong={true}
            className="ion-margin-vertical"
            disabled={loading}
            onClick={cancelEdit}
          >
            Cancel
          </IonButton>
          <IonButton
            expand="block"
            color="primary"
            type="submit"
            strong={true}
            disabled={!formState.isValid || loading}
            onClick={handleSubmit(onSubmit)}
          >
            {loading ? <IonSpinner name="dots" /> : 'Update'}
          </IonButton>
        </IonToolbar>
      </>
    );
  }

  return (
    <IonPage>
      {platformType === HKPlatform.DESKTOP && isDesktopWidth
        ? DesktopEditAccountView()
        : MobileEditAccountView()}
      <IonModal
        className={`hk-discard-account-changes-modal ${
          platformType === HKPlatform.DESKTOP && isDesktopWidth
            ? 'hk-desktop-discard-account-changes-modal'
            : ''
        }`}
        isOpen={discardModalVisible}
        backdropDismiss={false}
        onDidDismiss={() => dispatch(hideDiscardModal())}
      >
        <DiscardChanges></DiscardChanges>
      </IonModal>
    </IonPage>
  );
};

export default EditAccount;
